import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import RAGBadge from '../components/RAGBadge';
import Sidebar from '../components/Sidebar';
import { useMobile } from '../hooks/useMobile';
import { usePermissions } from '../hooks/usePermissions';
import BrokerMessageThread from '../components/BrokerMessageThread';
import '../styles/DealDetails.css';

const STATUS_LABELS = { pipeline: 'Pipeline', payable: 'Payable', paid: 'Paid', cancelled: 'Cancelled' };

// Maps deal product_type to stage template product_type
const DEAL_TYPE_TO_STAGE_TYPE = {
  residential_purchase:   'residential',
  residential_remortgage: 'residential',
  buy_to_let_purchase:    'btl',
  buy_to_let_remortgage:  'btl',
  commercial:             'commercial',
  bridging:               'bridging',
  self_employed:          'self_employed',
  protection:             'protection',
  other:                  null,
};
const TIER_LABELS   = { company: 'Company', manager: 'Manager', adviser: 'Adviser' };
const COMMISSION_STAGES = ['pipeline', 'payable', 'paid', 'cancelled'];
const safeNum = (val) => parseFloat(val) || 0;

// ── Inline-editable field ─────────────────────────────────────
function InlineField({ label, field, value, type = 'text', editField, editVal, setEditVal, onStart, onSave, savedField, options }) {
  const isEditing = editField === field;
  const wasSaved  = savedField === field;
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#a0aec0', marginBottom: 4 }}>{label}</div>
      {isEditing ? (
        options ? (
          <select
            autoFocus
            value={editVal}
            onChange={e => setEditVal(e.target.value)}
            onBlur={() => onSave(field, editVal)}
            style={{ width: '100%', padding: '7px 10px', border: '1px solid #10b981', borderRadius: 6, fontSize: 14, outline: 'none', fontFamily: 'inherit' }}
          >
            {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        ) : (
          <input
            autoFocus
            type={type}
            value={editVal}
            onChange={e => setEditVal(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') onSave(field, editVal); if (e.key === 'Escape') onStart(null, ''); }}
            onBlur={() => onSave(field, editVal)}
            style={{ width: '100%', padding: '7px 10px', border: '1px solid #10b981', borderRadius: 6, fontSize: 14, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' }}
          />
        )
      ) : (
        <div
          onClick={() => onStart(field, value || '')}
          title="Click to edit"
          style={{ fontSize: 14, color: value ? '#0e1b2c' : '#cbd5e0', cursor: 'text', padding: '5px 8px', borderRadius: 6, display: 'flex', alignItems: 'center', gap: 6, background: '#f8fafc', border: '1px solid transparent', transition: 'border-color 0.15s' }}
          onMouseEnter={e => e.currentTarget.style.borderColor = '#CBD5E0'}
          onMouseLeave={e => e.currentTarget.style.borderColor = 'transparent'}
        >
          <span style={{ flex: 1 }}>
            {field === 'client_phone' && value
              ? <a href={`tel:${value}`} onClick={e => e.stopPropagation()} style={{ color: '#0e1b2c', textDecoration: 'none', fontWeight: 600 }}>{value}</a>
              : field === 'client_email' && value
                ? <a href={`mailto:${value}`} onClick={e => e.stopPropagation()} style={{ color: '#0e1b2c', textDecoration: 'none' }}>{value}</a>
                : value || <span style={{ color: '#a0aec0', fontStyle: 'italic' }}>Click to add</span>
            }
          </span>
          {wasSaved
            ? <span style={{ fontSize: 11, color: '#10b981', fontWeight: 700, flexShrink: 0 }}>✓</span>
            : <span style={{ fontSize: 10, color: '#cbd5e0', flexShrink: 0 }}>✏</span>
          }
        </div>
      )}
    </div>
  );
}

export default function DealDetails() {
  const { dealId } = useParams();
  const navigate   = useNavigate();
  const mobile     = useMobile();
  const perms      = usePermissions();

  // ── Core deal state ───────────────────────────────────────
  const [deal, setDeal]           = useState(null);
  const [allStages, setAllStages] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');

  // ── Reassign to a different adviser (broker owner only) ────
  const [showReassign, setShowReassign]   = useState(false);
  const [advisers, setAdvisers]           = useState([]);
  const [advisersLoading, setAdvisersLoading] = useState(false);
  const [reassignBusy, setReassignBusy]   = useState(false);
  const [reassignError, setReassignError] = useState('');

  // ── Inline editing ────────────────────────────────────────
  const [editField, setEditField]   = useState(null);
  const [editVal, setEditVal]       = useState('');
  const [savedField, setSavedField] = useState(null);
  const [savingField, setSavingField] = useState(false);

  // ── Commission stage ──────────────────────────────────────
  const [stageSaving, setStageSaving] = useState(false);

  // ── Fee override ──────────────────────────────────────────
  const [showOverride, setShowOverride]   = useState(false);
  const [overrideForm, setOverrideForm]   = useState({ fee_override: '', override_reason: '' });
  const [submitting, setSubmitting]       = useState(false);
  const [busyAllocId, setBusyAllocId]     = useState(null);

  // ── Notes ─────────────────────────────────────────────────
  const [notes, setNotes]             = useState([]);
  const [newNote, setNewNote]         = useState('');
  const [noteLoading, setNoteLoading] = useState(false);
  const noteInputRef = useRef();

  // ── Deal status ───────────────────────────────────────────
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusNote, setStatusNote]           = useState('');
  const [statusBusy, setStatusBusy]           = useState(false);
  const [statusError, setStatusError]         = useState('');

  // ── Stage change ──────────────────────────────────────────
  const [stageChanging, setStageChanging] = useState(false);

  // ── DIP ───────────────────────────────────────────────────
  const [dipDoc, setDipDoc]             = useState(null);
  const [showDipPanel, setShowDipPanel] = useState(false);
  const [dipForm, setDipForm]           = useState({ reference: '', expiry: '', visible: false });
  const [dipUploading, setDipUploading] = useState(false);
  const [dipFile, setDipFile]           = useState(null);
  const dipFileRef = React.useRef();

  // ── Actions ───────────────────────────────────────────────
  const [actions, setActions]               = useState([]);
  const [showActionForm, setShowActionForm] = useState(false);
  const [actionForm, setActionForm]         = useState({ title: '', due_date: '', notes: '' });
  const [actionSaving, setActionSaving]     = useState(false);

  // ── Stage history ─────────────────────────────────────────
  const [stageHistory, setStageHistory] = useState([]);

  // ── Client portal ─────────────────────────────────────────
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteDocTypes, setInviteDocTypes]   = useState([]);
  const [selectedDocs, setSelectedDocs]       = useState([]);
  const [inviting, setInviting]               = useState(false);
  const [inviteSuccess, setInviteSuccess]     = useState('');
  const [inviteError, setInviteError]         = useState('');
  const [clientDocs, setClientDocs]           = useState(null);
  const [portalMsgs, setPortalMsgs]           = useState([]);
  const [newMsg, setNewMsg]                   = useState('');
  const [sendingMsg, setSendingMsg]           = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(null);
  const [rejectNote, setRejectNote]           = useState('');
  const [reviewBusy, setReviewBusy]           = useState({});
  const [showBrokerUpload, setShowBrokerUpload]   = useState(false);
  const [brokerUploadLabel, setBrokerUploadLabel] = useState('');
  const [brokerUploading, setBrokerUploading]     = useState(false);
  const [reminderSent, setReminderSent]           = useState(false);
  const [docInviteEnabled, setDocInviteEnabled]   = useState(false); // platform-owner gate
  const [docInviteStatusLoaded, setDocInviteStatusLoaded] = useState(false);
  const brokerFileRef = React.useRef();

  // -- Additional clients (partner / co-applicant / friend, up to 4) --
  const [additionalClients, setAdditionalClients] = useState([]);
  const [showAddClient, setShowAddClient] = useState(false);
  const [addClientForm, setAddClientForm] = useState({ name: '', relationship: 'Partner', email: '', phone: '', address: '' });
  const [addClientBusy, setAddClientBusy] = useState(false);
  const [addClientError, setAddClientError] = useState('');
  const [editingClientField, setEditingClientField] = useState(null); // "clientId:field"
  const [editingClientVal, setEditingClientVal] = useState('');
  const [inviteTargetClientId, setInviteTargetClientId] = useState(null); // null = primary client
  const [inviteTargetName, setInviteTargetName] = useState('');
  // Redesign 6 Aug 2026: cards were permanently-expanded (all 4 fields always
  // shown, even empty ones as full-width "Click to add" boxes) and the
  // section stretched to match the Client card's height via flex:1 — for a
  // deal with just one partner and no phone/email/address yet, that put a
  // near-empty card taking up half the screen. Now compact by default (one
  // client = one row), expand-to-edit on click.
  const [expandedClientId, setExpandedClientId] = useState(null);
  // Redesign 6 Aug 2026: the primary client had a permanently-expanded
  // card while additional clients collapsed to a row -- an unintended
  // hierarchy between clients on the same deal. Now folds the same way,
  // same default (collapsed), so a deal with a partner/co-applicant reads
  // as one consistent list rather than "the real client" plus an add-on.
  const [clientExpanded, setClientExpanded] = useState(false);
  const clearIntroducerUnread = useCallback(() => {
    setDeal(d => (d ? { ...d, unread_introducer_messages: 0 } : d));
  }, []);

  // ── Partner Referrals ─────────────────────────────────────
  const [referrals, setReferrals]             = React.useState([]);
  const [availablePartners, setAvailablePartners] = React.useState([]);
  const [showRefForm, setShowRefForm]         = React.useState(false);
  const [refForm, setRefForm]                 = React.useState({ partner_id: '', client_name: '', client_disclosure: false, commission_amount: '', notes: '' });
  const [refSaving, setRefSaving]             = React.useState(false);
  const [refError, setRefError]               = React.useState('');
  const [refUpdating, setRefUpdating]         = React.useState({});

  // ── Load everything on mount ──────────────────────────────
  useEffect(() => {
    loadDeal(); loadStages(); loadNotes(); loadClientDocs();
    loadPortalMsgs(); loadDip(); loadActions(); loadStageHistory();
    loadDocInviteStatus(); loadAdditionalClients();
  }, [dealId]); // eslint-disable-line

  const loadAdditionalClients = async () => {
    try {
      const res = await fetch(`/api/deal-clients?deal_id=${dealId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      const data = await res.json();
      setAdditionalClients(Array.isArray(data.clients) ? data.clients : []);
    } catch { setAdditionalClients([]); }
  };

  const addAdditionalClient = async () => {
    if (!addClientForm.name.trim()) { setAddClientError('Name is required'); return; }
    setAddClientBusy(true); setAddClientError('');
    try {
      const res = await fetch('/api/deal-clients', {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ deal_id: dealId, ...addClientForm }),
      });
      const data = await res.json();
      if (!res.ok) { setAddClientError(data.error || 'Failed to add client'); return; }
      setAdditionalClients(c => [...c, data.client]);
      setAddClientForm({ name: '', relationship: 'Partner', email: '', phone: '', address: '' });
      setShowAddClient(false);
    } catch { setAddClientError('Something went wrong'); }
    finally { setAddClientBusy(false); }
  };

  const saveAdditionalClientField = async (clientId, field, value) => {
    setEditingClientField(null);
    try {
      const res = await fetch(`/api/deal-clients/${clientId}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ [field]: value }),
      });
      if (res.ok) {
        const data = await res.json();
        setAdditionalClients(cs => cs.map(c => c.id === clientId ? data.client : c));
      }
    } catch {}
  };

  const removeAdditionalClient = async (clientId) => {
    if (!window.confirm('Remove this client from the deal?')) return;
    try {
      const res = await fetch(`/api/deal-clients/${clientId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (res.ok) setAdditionalClients(cs => cs.filter(c => c.id !== clientId));
    } catch {}
  };

  // Platform-owner gate for "invite client to upload documents" (10 July
  // 2026) — was previously ungated. Fetched once per mount, same pattern as
  // /api/agreement-templates/workflow-status for the Invite Introducer flow.
  const loadDocInviteStatus = async () => {
    try {
      const res = await fetch('/api/deals/document-upload-invite-status', {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      const d = await res.json();
      setDocInviteEnabled(!!d.enabled);
    } catch {
      setDocInviteEnabled(false);
    } finally {
      setDocInviteStatusLoaded(true);
    }
  };

  const loadReferrals = React.useCallback(async () => {
    const r = await fetch('/api/partners/referrals/deal/' + dealId, { headers: { Authorization: 'Bearer ' + localStorage.getItem('token') } });
    if (r.ok) { const d = await r.json(); setReferrals(Array.isArray(d) ? d : []); }
  }, [dealId]);

  const loadAvailablePartners = React.useCallback(async () => {
    const r = await fetch('/api/partners/available', { headers: { Authorization: 'Bearer ' + localStorage.getItem('token') } });
    if (r.ok) { const d = await r.json(); setAvailablePartners(Array.isArray(d) ? d : []); }
  }, []);

  React.useEffect(() => { loadReferrals(); loadAvailablePartners(); }, [loadReferrals, loadAvailablePartners]);

  const loadDeal = async () => {
    try {
      const response = await fetch(`/api/deals/${dealId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
      });
      const json = await response.json();
      setDeal(json);
    } catch (err) { setError('Failed to load deal'); console.error(err); }
    finally { setLoading(false); }
  };

  // ── Reassign to a different adviser (broker owner only) ────
  const openReassign = async () => {
    setShowReassign(true);
    setReassignError('');
    if (advisers.length || !deal?.broker_company_id) return;
    setAdvisersLoading(true);
    try {
      const res = await fetch(`/api/users/company/${deal.broker_company_id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (res.ok) {
        const data = await res.json();
        const staff = (data.users || []).filter(
          u => (u.role === 'broker' || u.role === 'owner') && u.status === 'active'
        );
        setAdvisers(staff);
      }
    } catch {}
    finally { setAdvisersLoading(false); }
  };

  const reassignDeal = async (userId) => {
    if (!userId || userId === deal.created_by_user_id) { setShowReassign(false); return; }
    setReassignBusy(true); setReassignError('');
    try {
      const res = await fetch(`/api/deals/${dealId}/reassign`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to reassign deal');
      setShowReassign(false);
      await loadDeal();
    } catch (err) { setReassignError(err.message); }
    finally { setReassignBusy(false); }
  };

  const loadStages = async () => {
    try {
      const res = await fetch('/api/stages', { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      const data = await res.json();
      setAllStages(data.stages || []);
    } catch {}
  };

  const loadNotes = async () => {
    try {
      const res = await fetch(`/api/deals/${dealId}/notes`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      const data = await res.json();
      setNotes(data.notes || []);
    } catch {}
  };

  const loadDip = async () => {
    try {
      const res = await fetch(`/api/deals/${dealId}/dip`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      if (res.ok) { const d = await res.json(); setDipDoc(d.dip || null); }
    } catch {}
  };

  const loadStageHistory = async () => {
    try {
      const res = await fetch(`/api/deals/${dealId}/stage-history`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      if (res.ok) { const d = await res.json(); setStageHistory(d.history || []); }
    } catch {}
  };

  const loadActions = async () => {
    try {
      const res = await fetch(`/api/deals/${dealId}/actions`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      if (res.ok) { const d = await res.json(); setActions(d.actions || []); }
    } catch {}
  };

  const loadClientDocs = async () => {
    try {
      const res = await fetch(`/api/deals/${dealId}/client-documents`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      if (res.ok) { const d = await res.json(); setClientDocs(d); }
    } catch {}
  };

  const loadPortalMsgs = async () => {
    try {
      const res = await fetch(`/api/deals/${dealId}/portal-messages`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      if (res.ok) { const d = await res.json(); setPortalMsgs(d.messages || []); }
    } catch {}
  };

  // ── Inline field save ─────────────────────────────────────
  const startEdit = (field, val) => { setEditField(field); setEditVal(val || ''); };

  const saveInlineField = async (field, value) => {
    if (value === (deal[field] || '')) { setEditField(null); return; }
    setSavingField(true);
    try {
      const body = { [field]: value };
      // If product type changes, clear deal_stage_id if the current stage belongs to a different product
      if (field === 'product_type' && deal.deal_stage_id) {
        const currentStage = allStages.find(s => s.id === deal.deal_stage_id);
        const newStageType = DEAL_TYPE_TO_STAGE_TYPE[value] ?? value;
        if (currentStage && currentStage.product_type !== newStageType) {
          body.deal_stage_id = null;
        }
      }
      const res = await fetch(`/api/deals/${dealId}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        await loadDeal();
        setSavedField(field);
        setTimeout(() => setSavedField(null), 2000);
      }
    } catch {}
    finally { setSavingField(false); setEditField(null); }
  };

  // ── Commission stage update ───────────────────────────────
  const updateCommissionStage = async (stage) => {
    if (stage === deal.stage) return;
    setStageSaving(true);
    try {
      await fetch(`/api/deals/${dealId}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ stage }),
      });
      await loadDeal();
    } catch {}
    finally { setStageSaving(false); }
  };

  // ── Deal status ───────────────────────────────────────────
  const updateStatus = async (status) => {
    setStatusBusy(true); setStatusError('');
    try {
      const res = await fetch('/api/deals/' + dealId + '/status', {
        method: 'PATCH',
        headers: { Authorization: 'Bearer ' + localStorage.getItem('token'), 'Content-Type': 'application/json' },
        body: JSON.stringify({ status, note: statusNote || undefined }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed');
      setShowStatusModal(false); setStatusNote('');
      loadDeal();
    } catch (err) { setStatusError(err.message); }
    finally { setStatusBusy(false); }
  };

  // ── Stage change ──────────────────────────────────────────
  const changeStage = async (stage) => {
    if (stage.id === deal.deal_stage_id) return;
    const msg = stage.triggers_email
      ? `Move deal to "${stage.name}"?\n\nThis will send an email notification to the introducer.`
      : `Move deal to "${stage.name}"?`;
    if (!window.confirm(msg)) return;
    setStageChanging(true); setError('');
    try {
      const res = await fetch(`/api/deals/${dealId}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ deal_stage_id: stage.id }),
      });
      if (!res.ok) { const b = await res.json().catch(() => ({})); throw new Error(b.error || 'Failed to change stage'); }
      await loadDeal(); loadStageHistory();
    } catch (err) { setError(err.message || 'Failed to change stage'); }
    finally { setStageChanging(false); }
  };

  // ── DIP ───────────────────────────────────────────────────
  const uploadDip = async () => {
    if (!dipFile) return;
    setDipUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', dipFile);
      fd.append('reference', dipForm.reference);
      fd.append('expiry', dipForm.expiry);
      fd.append('visible_to_introducer', dipForm.visible ? 'true' : 'false');
      const res = await fetch(`/api/deals/${dealId}/dip`, {
        method: 'POST', headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }, body: fd,
      });
      if (!res.ok) { const b = await res.json().catch(() => ({})); throw new Error(b.error || 'Upload failed'); }
      await loadDip(); setShowDipPanel(false); setDipFile(null);
      setDipForm({ reference: '', expiry: '', visible: false });
    } catch (err) { setError(err.message || 'DIP upload failed'); }
    finally { setDipUploading(false); }
  };

  // ── Actions ───────────────────────────────────────────────
  const saveAction = async () => {
    if (!actionForm.title.trim()) return;
    setActionSaving(true);
    try {
      const res = await fetch(`/api/deals/${dealId}/actions`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(actionForm),
      });
      if (!res.ok) throw new Error('Failed to save action');
      setActionForm({ title: '', due_date: '', notes: '' });
      setShowActionForm(false);
      await loadActions();
    } catch (err) { setError(err.message); }
    finally { setActionSaving(false); }
  };

  const toggleAction = async (actionId, completed) => {
    try {
      await fetch(`/api/deals/${dealId}/actions/${actionId}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ completed }),
      });
      await loadActions();
    } catch {}
  };

  const deleteAction = async (actionId) => {
    if (!window.confirm('Delete this action?')) return;
    try {
      await fetch(`/api/deals/${dealId}/actions/${actionId}`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      await loadActions();
    } catch {}
  };

  // ── Notes ─────────────────────────────────────────────────
  const submitNote = async () => {
    if (!newNote.trim()) return;
    setNoteLoading(true);
    try {
      const res = await fetch(`/api/deals/${dealId}/notes`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ note: newNote.trim() }),
      });
      if (res.ok) { setNewNote(''); loadNotes(); }
    } catch {}
    finally { setNoteLoading(false); }
  };

  const deleteNote = async (noteId) => {
    try {
      await fetch(`/api/deals/${dealId}/notes/${noteId}`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      loadNotes();
    } catch {}
  };

  // ── Client portal ─────────────────────────────────────────
  const openInviteModal = async (targetClientId = null, targetName = '') => {
    setInviteSuccess(''); setInviteError('');
    setInviteTargetClientId(targetClientId);
    setInviteTargetName(targetName);
    try {
      const res = await fetch(`/api/admin/document-types`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      const data = await res.json();
      const types = data.documentTypes || data;
      setInviteDocTypes(types);
      setSelectedDocs(types.filter(t => t.is_required).map(t => t.id));
    } catch { setInviteDocTypes([]); setSelectedDocs([]); }
    setShowInviteModal(true);
  };

  const sendInvite = async () => {
    if (!selectedDocs.length) { setInviteError('Please select at least one document type.'); return; }
    setInviting(true); setInviteError('');
    try {
      const url = inviteTargetClientId
        ? `/api/deal-clients/${inviteTargetClientId}/invite`
        : `/api/deals/${dealId}/invite-client`;
      const res = await fetch(url, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ documentTypeIds: selectedDocs }),
      });
      const data = await res.json();
      if (!res.ok) setInviteError(data.error || 'Failed to send invite.');
      else {
        setInviteSuccess('Invite sent! The client will receive an email with a secure link.');
        loadClientDocs(); loadAdditionalClients();
      }
    } catch { setInviteError('Something went wrong.'); }
    finally { setInviting(false); }
  };

  const sendPortalMsg = async () => {
    if (!newMsg.trim()) return;
    setSendingMsg(true);
    try {
      await fetch(`/api/deals/${dealId}/portal-messages`, {
        method: 'POST', headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: newMsg.trim() }),
      });
      setNewMsg(''); loadPortalMsgs();
    } catch {} finally { setSendingMsg(false); }
  };

  const reviewDoc = async (docId, action, note) => {
    setReviewBusy(b => ({ ...b, [docId]: true }));
    try {
      await fetch(`/api/deals/${dealId}/client-documents/${docId}/review`, {
        method: 'PATCH', headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ action, note }),
      });
      setShowRejectModal(null); setRejectNote(''); loadClientDocs();
    } catch {} finally { setReviewBusy(b => ({ ...b, [docId]: false })); }
  };

  const sendReminder = async () => {
    try {
      const res = await fetch(`/api/deals/${dealId}/send-document-reminder`, {
        method: 'POST', headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (res.ok) { setReminderSent(true); setTimeout(() => setReminderSent(false), 4000); }
      else { const d = await res.json().catch(() => ({})); alert(d.error || 'Could not send reminder.'); }
    } catch { alert('Network error sending reminder'); }
  };

  const brokerUpload = async (file) => {
    if (!file) return;
    setBrokerUploading(true);
    const form = new FormData();
    form.append('file', file);
    if (brokerUploadLabel.trim()) form.append('label', brokerUploadLabel.trim());
    try {
      await fetch(`/api/deals/${dealId}/client-portal/broker-upload`, {
        method: 'POST', headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }, body: form,
      });
      setBrokerUploadLabel(''); setShowBrokerUpload(false); loadClientDocs();
    } catch {} finally { setBrokerUploading(false); }
  };

  const dlDoc = async (docId, filename) => {
    const res = await fetch(`/api/deals/${dealId}/client-documents/${docId}/download`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
    if (!res.ok) return;
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = filename || 'document'; a.click(); URL.revokeObjectURL(url);
  };

  // ── Fee override ──────────────────────────────────────────
  const handleSubmitOverride = async (e) => {
    e.preventDefault();
    if (!overrideForm.fee_override || !overrideForm.override_reason) { setError('Please fill in all fields'); return; }
    setSubmitting(true);
    try {
      const response = await fetch(`/api/deals/${dealId}/override-fee`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ fee_override: parseFloat(overrideForm.fee_override), override_reason: overrideForm.override_reason }),
      });
      if (!response.ok) throw new Error('Failed to override fee');
      await loadDeal(); setShowOverride(false); setOverrideForm({ fee_override: '', override_reason: '' });
    } catch (err) { setError(err.message || 'Failed to override fee'); }
    finally { setSubmitting(false); }
  };

  // ── Commission allocations ────────────────────────────────
  const updateAllocationStatus = async (allocId, newStatus, opts = {}) => {
    const { confirmMessage } = opts;
    if (confirmMessage && !window.confirm(confirmMessage)) return;
    setBusyAllocId(allocId); setError('');
    try {
      const response = await fetch(`/api/deals/${dealId}/allocations/${allocId}/status`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      });
      if (!response.ok) { const body = await response.json().catch(() => ({})); throw new Error(body.error || 'Failed to update allocation'); }
      await loadDeal();
    } catch (err) { setError(err.message || 'Failed to update allocation'); }
    finally { setBusyAllocId(null); }
  };

  // ── Partner Referrals ─────────────────────────────────────
  const submitReferral = async (e) => {
    e.preventDefault(); setRefError('');
    if (!refForm.client_disclosure) { setRefError('You must confirm client disclosure.'); return; }
    setRefSaving(true);
    const body = { ...refForm };
    if (body.commission_amount) body.commission_amount = parseFloat(body.commission_amount);
    const r = await fetch('/api/partners/referrals/deal/' + dealId, {
      method: 'POST',
      headers: { Authorization: 'Bearer ' + localStorage.getItem('token'), 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const d = await r.json();
    setRefSaving(false);
    if (!r.ok) { setRefError(d.error || 'Failed'); return; }
    setShowRefForm(false);
    setRefForm({ partner_id: '', client_name: '', client_disclosure: false, commission_amount: '', notes: '' });
    loadReferrals();
  };

  const updateReferralStatus = async (refId, status) => {
    setRefUpdating(u => ({ ...u, [refId]: true }));
    await fetch('/api/partners/referrals/' + refId, {
      method: 'PATCH',
      headers: { Authorization: 'Bearer ' + localStorage.getItem('token'), 'Content-Type': 'application/json' },
      body: JSON.stringify({ status }),
    });
    await loadReferrals();
    setRefUpdating(u => ({ ...u, [refId]: false }));
  };

  // ── Loading / not found ───────────────────────────────────
  if (loading) return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f8f9fa' }}>
      <Sidebar />
      <main style={{ flex: 1, marginLeft: mobile ? 0 : 70, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#718096', fontSize: 16 }}>Loading…</main>
    </div>
  );
  if (!deal) return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f8f9fa' }}>
      <Sidebar />
      <main style={{ flex: 1, marginLeft: mobile ? 0 : 70, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#718096', fontSize: 16 }}>Deal not found</main>
    </div>
  );

  const allocations    = deal.allocations || [];
  const allocTotal     = allocations.filter(a => a.status !== 'cancelled').reduce((sum, a) => sum + safeNum(a.amount), 0);
  // DEAL_TYPE_TO_STAGE_TYPE maps 'other' to null deliberately (show all of the
  // company's stages, unfiltered). `??` treats that null as "no mapping" and
  // falls through to the raw product_type instead, so it must be checked with
  // `in` rather than `??` or an 'other'-typed deal never matches any stage
  // template and silently falls back to the generic hardcoded stage list.
  const stageProductType = deal?.product_type
    ? (deal.product_type in DEAL_TYPE_TO_STAGE_TYPE ? DEAL_TYPE_TO_STAGE_TYPE[deal.product_type] : deal.product_type)
    : null;
  const filteredStages = stageProductType ? allStages.filter(s => s.product_type === stageProductType) : allStages;
  const currentStageIdx = filteredStages.findIndex(s => s.id === deal.deal_stage_id);
  const openActions    = actions.filter(a => !a.completed);

  const PRODUCT_LABELS = {
    residential_purchase: 'Residential Purchase', residential_remortgage: 'Residential Remortgage',
    buy_to_let_purchase: 'BTL Purchase', buy_to_let_remortgage: 'BTL Remortgage',
    commercial: 'Commercial', bridging: 'Bridging', other: 'Other',
  };

  const sectionStyle = { background: '#fff', border: '1px solid #E2E8F0', borderRadius: 10, marginBottom: 16, overflow: 'hidden' };
  const sectionHead  = { padding: '14px 20px', borderBottom: '1px solid #EDF2F7', display: 'flex', alignItems: 'center', justifyContent: 'space-between' };
  const sectionBody  = { padding: '16px 20px' };

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f8f9fa' }}>
      <Sidebar />
      <div style={{ flex: 1, marginLeft: mobile ? 0 : 70, padding: mobile ? '16px' : '24px 32px', boxSizing: 'border-box', minWidth: 0 }}>

        {/* ── Header ── */}
        <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <button onClick={() => navigate('/commission-dashboard')}
              style={{ background: 'none', border: '1px solid #CBD5E0', borderRadius: 8, padding: '7px 14px', cursor: 'pointer', fontSize: 13, color: '#718096' }}>
              ← Back
            </button>
            <div>
              <h1 style={{ margin: 0, fontSize: 20, color: '#0e1b2c', fontWeight: 700 }}>
                {[deal.client_name, ...additionalClients.map(ac => ac.name)].filter(Boolean).join(' & ')}
              </h1>
              {deal.reference && <span style={{ fontSize: 12, fontWeight: 600, color: '#10b981', letterSpacing: '0.04em' }}>{deal.reference}</span>}
              {deal.introducer_name && <span style={{ fontSize: 12, color: '#a0aec0', marginLeft: 8 }}>via {deal.introducer_name}</span>}
              <div style={{ fontSize: 12, color: '#718096', marginTop: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                Assigned to: <strong style={{ color: '#0e1b2c' }}>{deal.created_by_name || 'Unassigned'}</strong>
                {(perms.isBrokerOwner || perms.isPlatformOwner) && (
                  <button onClick={openReassign}
                    style={{ background: 'none', border: 'none', color: '#1a56db', fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: '2px 4px' }}>
                    Change
                  </button>
                )}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {deal.current_status_code === 'not_proceeding' && <>
              <button onClick={() => setShowStatusModal('archive')} style={{ background: '#dc2626', color: '#fff', border: 'none', borderRadius: 6, padding: '7px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Archive</button>
              <button onClick={() => { setStatusNote(''); updateStatus('active'); }} style={{ background: '#10b981', color: '#fff', border: 'none', borderRadius: 6, padding: '7px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Reopen</button>
            </>}
            {deal.current_status_code === 'archived' && (
              <button onClick={() => { setStatusNote(''); updateStatus('active'); }} style={{ background: '#10b981', color: '#fff', border: 'none', borderRadius: 6, padding: '7px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Reopen Deal</button>
            )}
            {!deal.current_status_code && (
              <button onClick={() => { setStatusNote(''); setStatusError(''); setShowStatusModal('not_proceeding'); }}
                style={{ background: '#f59e0b', color: '#fff', border: 'none', borderRadius: 6, padding: '7px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                Not Proceeding
              </button>
            )}
          </div>
        </div>

        {error && <div className="error-banner" style={{ marginBottom: 12 }}>{error}</div>}

        {/* ── Remortgage alert ── */}
        {deal.mortgage_end_date && (() => {
          const endDate = new Date(deal.mortgage_end_date);
          const daysLeft = Math.ceil((endDate - new Date()) / 86400000);
          if (daysLeft > 90) return null;
          const isUrgent = daysLeft <= 30;
          return (
            <div style={{ background: isUrgent ? '#fee2e2' : '#fef3c7', border: `1px solid ${isUrgent ? '#fca5a5' : '#fcd34d'}`, borderLeft: `4px solid ${isUrgent ? '#ef4444' : '#f59e0b'}`, borderRadius: 8, padding: '10px 16px', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 10, fontSize: 13 }}>
              <span style={{ fontSize: 16 }}>{isUrgent ? '🔴' : '⚠️'}</span>
              <span><strong>Remortgage {daysLeft <= 0 ? 'TODAY' : `in ${daysLeft} day${daysLeft !== 1 ? 's' : ''}`}</strong> — {endDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
            </div>
          );
        })()}

        {/* ── Status banners ── */}
        {deal.current_status_code === 'not_proceeding' && (
          <div style={{ background: '#fef3c7', border: '1px solid #f59e0b', borderLeft: '4px solid #d97706', borderRadius: 8, padding: '10px 16px', marginBottom: 12 }}>
            <strong style={{ color: '#92400e' }}>⚠️ Not Proceeding</strong>
            {deal.status_note && <p style={{ margin: '3px 0 0', fontSize: 13, color: '#78350f' }}>{deal.status_note}</p>}
          </div>
        )}
        {deal.current_status_code === 'archived' && (
          <div style={{ background: '#f3f4f6', border: '1px solid #d1d5db', borderLeft: '4px solid #6b7280', borderRadius: 8, padding: '10px 16px', marginBottom: 12 }}>
            <strong style={{ color: '#374151' }}>📁 Archived</strong>
            {deal.status_note && <p style={{ margin: '3px 0 0', fontSize: 13, color: '#6b7280' }}>{deal.status_note}</p>}
          </div>
        )}

        {/* ── TOP TWO-COLUMN SECTION ── */}
        <div style={{ display: 'grid', gridTemplateColumns: mobile ? '1fr' : '320px 1fr', gap: 16, marginBottom: 16 }}>

          {/* Clients — one card, one consistent list (6 Aug 2026 redesign).
              Primary client and additional clients (partner/co-applicant/
              friend, up to 4) all render as equal, foldable rows — no card
              is bigger or more prominent than another. "+ Add Client" lives
              on the shared card header. */}
          <div style={sectionStyle}>
            <div style={sectionHead}>
              <span style={{ fontWeight: 700, fontSize: 15, color: '#0e1b2c' }}>Clients ({1 + additionalClients.length})</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                {savingField && <span style={{ fontSize: 11, color: '#10b981' }}>Saving…</span>}
                {additionalClients.length < 4 && (
                  <button onClick={() => { setShowAddClient(s => !s); setAddClientError(''); }}
                    style={{ background: 'none', border: '1px solid #1a56db', color: '#1a56db', borderRadius: 6, padding: '4px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                    {showAddClient ? 'Cancel' : '+ Add Client'}
                  </button>
                )}
              </div>
            </div>
            <div style={sectionBody}>
              {showAddClient && (
                <div style={{ marginBottom: 16, padding: 12, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8 }}>
                  <input placeholder="Full name *" value={addClientForm.name} onChange={e => setAddClientForm(f => ({ ...f, name: e.target.value }))}
                    style={{ width: '100%', boxSizing: 'border-box', padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, marginBottom: 8, fontFamily: 'inherit' }} />
                  <select value={addClientForm.relationship} onChange={e => setAddClientForm(f => ({ ...f, relationship: e.target.value }))}
                    style={{ width: '100%', boxSizing: 'border-box', padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, marginBottom: 8, fontFamily: 'inherit' }}>
                    <option value="Partner">Partner</option>
                    <option value="Co-applicant">Co-applicant</option>
                    <option value="Friend">Friend</option>
                    <option value="Other">Other</option>
                  </select>
                  <input placeholder="Email" value={addClientForm.email} onChange={e => setAddClientForm(f => ({ ...f, email: e.target.value }))}
                    style={{ width: '100%', boxSizing: 'border-box', padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, marginBottom: 8, fontFamily: 'inherit' }} />
                  <input placeholder="Phone" value={addClientForm.phone} onChange={e => setAddClientForm(f => ({ ...f, phone: e.target.value }))}
                    style={{ width: '100%', boxSizing: 'border-box', padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, marginBottom: 8, fontFamily: 'inherit' }} />
                  <input placeholder="Address" value={addClientForm.address} onChange={e => setAddClientForm(f => ({ ...f, address: e.target.value }))}
                    style={{ width: '100%', boxSizing: 'border-box', padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, marginBottom: 8, fontFamily: 'inherit' }} />
                  {addClientError && <div style={{ color: '#dc2626', fontSize: 12, marginBottom: 8 }}>{addClientError}</div>}
                  <button onClick={addAdditionalClient} disabled={addClientBusy}
                    style={{ width: '100%', background: '#10b981', color: 'white', border: 'none', borderRadius: 6, padding: '8px', fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: addClientBusy ? 0.6 : 1 }}>
                    {addClientBusy ? 'Adding…' : 'Add Client'}
                  </button>
                </div>
              )}

              {/* Primary client row — same shape/behaviour as an additional
                  client row below: badge, name, compact contact summary,
                  click to expand for full fields. */}
              <div style={{ marginBottom: 8, padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 8 }}>
                <div
                  onClick={() => setClientExpanded(v => !v)}
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', gap: 10 }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, flex: 1 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: '#4f46e5', background: '#eef2ff', padding: '2px 8px', borderRadius: 10, flexShrink: 0 }}>Primary</span>
                    <span style={{ fontSize: 13.5, fontWeight: 600, color: '#0e1b2c', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{deal.client_name || 'Client'}</span>
                    {[deal.client_phone, deal.client_email].filter(Boolean).length > 0 && (
                      <span style={{ fontSize: 12, color: '#94a3b8', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        · {[deal.client_phone, deal.client_email].filter(Boolean).join(' · ')}
                      </span>
                    )}
                  </div>
                  <span style={{ fontSize: 11, color: '#a0aec0', transform: clientExpanded ? 'rotate(180deg)' : 'none', display: 'inline-block', flexShrink: 0 }}>▾</span>
                </div>
                {clientExpanded && (
                  <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #f1f5f9' }}>
                    <InlineField label="Full Name" field="client_name" value={deal.client_name}
                      editField={editField} editVal={editVal} setEditVal={setEditVal}
                      onStart={startEdit} onSave={saveInlineField} savedField={savedField} />
                    <InlineField label="Phone" field="client_phone" value={deal.client_phone}
                      editField={editField} editVal={editVal} setEditVal={setEditVal}
                      onStart={startEdit} onSave={saveInlineField} savedField={savedField} />
                    <InlineField label="Email" field="client_email" value={deal.client_email} type="email"
                      editField={editField} editVal={editVal} setEditVal={setEditVal}
                      onStart={startEdit} onSave={saveInlineField} savedField={savedField} />
                    <InlineField label="Address" field="client_address" value={deal.client_address}
                      editField={editField} editVal={editVal} setEditVal={setEditVal}
                      onStart={startEdit} onSave={saveInlineField} savedField={savedField} />
                    <InlineField label="Product Type" field="product_type"
                      value={deal.product_type ? (PRODUCT_LABELS[deal.product_type] || deal.product_type) : ''}
                      editField={editField} editVal={editVal} setEditVal={setEditVal}
                      onStart={(f) => startEdit(f, deal.product_type || '')}
                      onSave={saveInlineField} savedField={savedField}
                      options={[
                        { value: '', label: '— Select —' },
                        { value: 'residential_purchase', label: 'Residential Purchase' },
                        { value: 'residential_remortgage', label: 'Residential Remortgage' },
                        { value: 'buy_to_let_purchase', label: 'BTL Purchase' },
                        { value: 'buy_to_let_remortgage', label: 'BTL Remortgage' },
                        { value: 'commercial', label: 'Commercial' },
                        { value: 'bridging', label: 'Bridging' },
                        { value: 'other', label: 'Other' },
                      ]}
                    />
                    <InlineField label="Mortgage End Date" field="mortgage_end_date"
                      value={deal.mortgage_end_date ? new Date(deal.mortgage_end_date).toLocaleDateString('en-GB') : ''}
                      editField={editField} editVal={editVal} setEditVal={setEditVal}
                      onStart={(f) => startEdit(f, deal.mortgage_end_date ? new Date(deal.mortgage_end_date).toISOString().slice(0, 10) : '')}
                      onSave={saveInlineField} savedField={savedField} type="date"
                    />
                    {deal.client_notes && (
                      <div style={{ marginTop: 8, padding: '8px 10px', background: '#fffef5', border: '1px solid #fef3c7', borderRadius: 6, fontSize: 13, color: '#4a5568' }}>
                        <span style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', color: '#a0aec0', display: 'block', marginBottom: 3 }}>Notes from create</span>
                        {deal.client_notes}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {additionalClients.length === 0 && !showAddClient && (
                <p style={{ fontSize: 13, color: '#a0aec0', fontStyle: 'italic' }}>
                  Most deals are one client. Add another for a couple, co-applicants, or friends buying together.
                </p>
              )}

              {additionalClients.map(ac => {
                const editKey = (field) => `${ac.id}:${field}`;
                const isExpanded = expandedClientId === ac.id;
                const fieldRow = (label, field, val, type = 'text') => (
                  <div style={{ marginBottom: 8 }}>
                    <div style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', color: '#a0aec0' }}>{label}</div>
                    {editingClientField === editKey(field) ? (
                      <input autoFocus type={type} value={editingClientVal} onChange={e => setEditingClientVal(e.target.value)}
                        onBlur={() => saveAdditionalClientField(ac.id, field, editingClientVal)}
                        onKeyDown={e => { if (e.key === 'Enter') saveAdditionalClientField(ac.id, field, editingClientVal); }}
                        style={{ width: '100%', boxSizing: 'border-box', padding: '5px 8px', border: '1px solid #10b981', borderRadius: 6, fontSize: 13, fontFamily: 'inherit' }} />
                    ) : (
                      <div onClick={() => { setEditingClientField(editKey(field)); setEditingClientVal(val || ''); }}
                        style={{ fontSize: 13, color: val ? '#0e1b2c' : '#cbd5e0', cursor: 'text', padding: '4px 6px', borderRadius: 6, background: '#f8fafc' }}>
                        {val || 'Click to add'}
                      </div>
                    )}
                  </div>
                );
                // Compact one-line contact summary for the collapsed row — only
                // shows what's actually filled in, instead of three empty
                // "Click to add" boxes every time.
                const contactBits = [ac.phone, ac.email].filter(Boolean);
                return (
                  <div key={ac.id} style={{ marginBottom: 8, padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 8 }}>
                    <div
                      onClick={() => setExpandedClientId(isExpanded ? null : ac.id)}
                      style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', gap: 10 }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, flex: 1 }}>
                        <span style={{ fontSize: 11, fontWeight: 700, color: '#4f46e5', background: '#eef2ff', padding: '2px 8px', borderRadius: 10, flexShrink: 0 }}>{ac.relationship}</span>
                        <span style={{ fontSize: 13.5, fontWeight: 600, color: '#0e1b2c', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{ac.name || 'Unnamed'}</span>
                        {contactBits.length > 0 && (
                          <span style={{ fontSize: 12, color: '#94a3b8', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>· {contactBits.join(' · ')}</span>
                        )}
                        {ac.has_portal_login && (
                          <span style={{ fontSize: 11, color: '#065f46', background: '#d1fae5', padding: '2px 8px', borderRadius: 20, fontWeight: 600, flexShrink: 0 }}>✓ Portal</span>
                        )}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
                        {(perms.isManager || perms.isBrokerOwner || perms.isPlatformOwner) && (
                          <button onClick={(e) => { e.stopPropagation(); removeAdditionalClient(ac.id); }} title="Remove"
                            style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 12 }}>Remove</button>
                        )}
                        <span style={{ fontSize: 11, color: '#a0aec0', transform: isExpanded ? 'rotate(180deg)' : 'none', display: 'inline-block' }}>▾</span>
                      </div>
                    </div>
                    {isExpanded && (
                      <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #f1f5f9' }}>
                        {fieldRow('Full Name', 'name', ac.name)}
                        {fieldRow('Phone', 'phone', ac.phone)}
                        {fieldRow('Email', 'email', ac.email, 'email')}
                        {fieldRow('Address', 'address', ac.address)}
                        <div style={{ marginTop: 8 }}>
                          {ac.has_portal_login ? (
                            <span style={{ fontSize: 12, color: '#065f46', background: '#d1fae5', padding: '4px 10px', borderRadius: 20, fontWeight: 600 }}>✓ Has portal login</span>
                          ) : docInviteEnabled && ac.email ? (
                            <button onClick={() => openInviteModal(ac.id, ac.name)}
                              style={{ background: '#1a56db', color: 'white', border: 'none', borderRadius: 8, padding: '6px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                              Send Portal Invite
                            </button>
                          ) : !ac.email ? (
                            <span style={{ fontSize: 11, color: '#a0aec0', fontStyle: 'italic' }}>Add an email to invite to the portal</span>
                          ) : null}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* RIGHT: Deal progress + tasks */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

            {/* Deal Progress */}
            <div style={sectionStyle}>
              <div style={sectionHead}>
                <span style={{ fontWeight: 700, fontSize: 15, color: '#0e1b2c' }}>Deal Progress</span>
                {deal.deal_stage_id && <RAGBadge stageName={deal.deal_stage_name} ragColour={deal.deal_stage_colour} />}
              </div>
              <div style={sectionBody}>
                {filteredStages.length > 0 ? (
                  <>
                    {!deal.deal_stage_id && (
                      <div style={{ marginBottom: 10, padding: '7px 12px', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 7, fontSize: 12, color: '#92400e' }}>
                        ⚠️ No stage assigned — click a stage below to assign this deal
                      </div>
                    )}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 0, overflowX: 'auto', paddingBottom: 4 }}>
                      {filteredStages.map((s, idx) => {
                        const isPast = idx < currentStageIdx, isCurrent = idx === currentStageIdx;
                        const RAG_DOT = { red: '#ef4444', amber: '#f59e0b', green: '#10b981' };
                        const dot = RAG_DOT[s.rag_colour] || '#f59e0b';
                        return (
                          <React.Fragment key={s.id}>
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 72 }}>
                              <div
                                title={`Move to: ${s.name}${s.triggers_email ? ' (will email introducer)' : ''}`}
                                onClick={() => !stageChanging && changeStage(s)}
                                style={{ width: isCurrent ? 18 : 12, height: isCurrent ? 18 : 12, borderRadius: '50%', background: isPast || isCurrent ? dot : '#E2E8F0', border: isCurrent ? `3px solid ${dot}` : isPast ? `2px solid ${dot}` : '2px solid #CBD5E0', boxShadow: isCurrent ? `0 0 0 3px ${dot}22` : 'none', flexShrink: 0, cursor: stageChanging || isCurrent ? 'default' : 'pointer', transition: 'all 0.2s' }}
                              />
                              <span style={{ fontSize: 10, marginTop: 5, color: isCurrent ? '#0e1b2c' : isPast ? '#4a5568' : '#a0aec0', fontWeight: isCurrent ? 700 : 400, textAlign: 'center', maxWidth: 64, lineHeight: 1.3 }}>{s.name}</span>
                            </div>
                            {idx < filteredStages.length - 1 && <div style={{ flex: 1, height: 2, background: idx < currentStageIdx ? dot : '#E2E8F0', minWidth: 12, marginBottom: 20, transition: 'background 0.2s' }} />}
                          </React.Fragment>
                        );
                      })}
                    </div>
                    <p style={{ margin: '8px 0 0', fontSize: 11, color: '#a0aec0' }}>
                      {stageChanging ? <span style={{ color: '#10b981' }}>Updating…</span> : 'Click a stage to advance'}
                    </p>
                  </>
                ) : (() => {
                  // Fallback: show simple stage progress when no custom templates are configured
                  const SIMPLE_STAGES = [
                    { key: 'pipeline',  label: 'Pipeline' },
                    { key: 'submitted', label: 'Submitted' },
                    { key: 'offered',   label: 'Offered' },
                    { key: 'completed', label: 'Completed' },
                  ];
                  const simpleStage = ['pipeline','submitted','offered','completed'].includes(deal.stage) ? deal.stage : 'pipeline';
                  const simpleIdx   = SIMPLE_STAGES.findIndex(s => s.key === simpleStage);
                  return (
                    <>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 0, paddingBottom: 4 }}>
                        {SIMPLE_STAGES.map((s, idx) => {
                          const isPast = idx < simpleIdx, isCurrent = idx === simpleIdx;
                          const dot = isCurrent ? '#10b981' : '#10b981';
                          return (
                            <React.Fragment key={s.key}>
                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 72 }}>
                                <div
                                  title={`Move to: ${s.label}`}
                                  onClick={() => { if (!stageChanging && !isCurrent) { setStageChanging(true); fetch(`/api/deals/${dealId}`, { method: 'PATCH', headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ stage: s.key }) }).then(() => loadDeal()).finally(() => setStageChanging(false)); } }}
                                  style={{ width: isCurrent ? 18 : 12, height: isCurrent ? 18 : 12, borderRadius: '50%', background: isPast || isCurrent ? '#10b981' : '#E2E8F0', border: isCurrent ? '3px solid #10b981' : isPast ? '2px solid #10b981' : '2px solid #CBD5E0', boxShadow: isCurrent ? '0 0 0 3px #10b98122' : 'none', flexShrink: 0, cursor: isCurrent ? 'default' : 'pointer', transition: 'all 0.2s' }}
                                />
                                <span style={{ fontSize: 10, marginTop: 5, color: isCurrent ? '#0e1b2c' : isPast ? '#4a5568' : '#a0aec0', fontWeight: isCurrent ? 700 : 400, textAlign: 'center', maxWidth: 64, lineHeight: 1.3 }}>{s.label}</span>
                              </div>
                              {idx < SIMPLE_STAGES.length - 1 && <div style={{ flex: 1, height: 2, background: idx < simpleIdx ? '#10b981' : '#E2E8F0', minWidth: 12, marginBottom: 20, transition: 'background 0.2s' }} />}
                            </React.Fragment>
                          );
                        })}
                      </div>
                      <p style={{ margin: '8px 0 0', fontSize: 11, color: '#a0aec0' }}>
                        {stageChanging ? <span style={{ color: '#10b981' }}>Updating…</span> : 'Click a stage to advance'}
                      </p>
                    </>
                  );
                })()}
              </div>
            </div>

            {/* Tasks (renamed from Actions, 6 Aug 2026) */}
            <div style={sectionStyle}>
              <div style={sectionHead}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontWeight: 700, fontSize: 15, color: '#0e1b2c' }}>Tasks</span>
                  {openActions.length > 0 && <span style={{ background: '#fee2e2', color: '#991b1b', fontSize: 12, fontWeight: 600, padding: '2px 8px', borderRadius: 9999 }}>{openActions.length} open</span>}
                </div>
                <button onClick={() => setShowActionForm(!showActionForm)} style={{ background: '#1a56db', color: '#fff', border: 'none', borderRadius: 8, padding: '7px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>+ Add Task</button>
              </div>
              <div style={sectionBody}>
                {showActionForm && (
                  <div style={{ background: '#f8fafc', borderRadius: 8, padding: 16, marginBottom: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <input value={actionForm.title} onChange={e => setActionForm({ ...actionForm, title: e.target.value })}
                      placeholder="Task title (e.g. Chase lender for offer letter)" autoFocus
                      style={{ padding: '9px 12px', border: '1px solid #E2E8F0', borderRadius: 6, fontSize: 14, outline: 'none' }} />
                    <div style={{ display: 'flex', gap: 10 }}>
                      <div style={{ flex: 1 }}>
                        <label style={{ fontSize: 12, color: '#718096', display: 'block', marginBottom: 3 }}>Due date</label>
                        <input type="date" value={actionForm.due_date} onChange={e => setActionForm({ ...actionForm, due_date: e.target.value })}
                          style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', border: '1px solid #E2E8F0', borderRadius: 6, fontSize: 13 }} />
                      </div>
                    </div>
                    <textarea value={actionForm.notes} onChange={e => setActionForm({ ...actionForm, notes: e.target.value })}
                      placeholder="Optional notes…" rows={2}
                      style={{ padding: '8px 12px', border: '1px solid #E2E8F0', borderRadius: 6, fontSize: 13, resize: 'vertical', fontFamily: 'inherit' }} />
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button onClick={saveAction} disabled={!actionForm.title.trim() || actionSaving}
                        style={{ background: !actionForm.title.trim() || actionSaving ? '#a0aec0' : '#1a56db', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 18px', fontSize: 13, fontWeight: 600, cursor: !actionForm.title.trim() || actionSaving ? 'not-allowed' : 'pointer' }}>
                        {actionSaving ? 'Saving…' : 'Save Task'}
                      </button>
                      <button onClick={() => setShowActionForm(false)} style={{ background: '#f3f4f6', border: 'none', borderRadius: 8, padding: '8px 14px', fontSize: 13, cursor: 'pointer', color: '#374151' }}>Cancel</button>
                    </div>
                  </div>
                )}
                {actions.length === 0 && !showActionForm ? (
                  <div style={{ textAlign: 'center', color: '#a0aec0', fontSize: 13, padding: '16px 0' }}>No tasks yet.</div>
                ) : actions.map(a => {
                  const overdue = a.due_date && !a.completed && new Date(a.due_date) < new Date();
                  return (
                    <div key={a.id} style={{ padding: '10px 0', borderTop: '1px solid #EDF2F7', display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                      <input type="checkbox" checked={!!a.completed} onChange={e => toggleAction(a.id, e.target.checked)}
                        style={{ marginTop: 2, width: 16, height: 16, cursor: 'pointer', accentColor: '#10b981', flexShrink: 0 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 14, color: a.completed ? '#a0aec0' : '#0e1b2c', textDecoration: a.completed ? 'line-through' : 'none', fontWeight: 500 }}>{a.title}</div>
                        {a.notes && <div style={{ fontSize: 12, color: '#718096', marginTop: 2 }}>{a.notes}</div>}
                        {a.due_date && <div style={{ fontSize: 11, marginTop: 3, color: overdue ? '#ef4444' : '#a0aec0', fontWeight: overdue ? 600 : 400 }}>{overdue ? '⚠ Overdue · ' : 'Due '}{new Date(a.due_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</div>}
                      </div>
                      <button onClick={() => deleteAction(a.id)} style={{ background: 'none', border: 'none', color: '#e74c3c', cursor: 'pointer', fontSize: 12, padding: '0 4px', flexShrink: 0 }}>✕</button>
                    </div>
                  );
                })}
              </div>
            </div>

          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: mobile ? '1fr' : '1fr 1fr', gap: 16, marginBottom: 16 }}>
        {/* ── Notes ── (merged Quick Note + Activity & Notes, 6 Aug 2026) */}
        <div style={sectionStyle}>
          <div style={sectionHead}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontWeight: 700, fontSize: 15, color: '#0e1b2c' }}>Notes</span>
              <span style={{ background: '#EDF2F7', color: '#718096', fontSize: 12, fontWeight: 600, padding: '2px 8px', borderRadius: 9999 }}>{notes.length}</span>
            </div>
            <span style={{ fontSize: 11, color: '#a0aec0' }}>Ctrl+Enter to save</span>
          </div>
          <div style={{ padding: '12px 16px 4px', display: 'flex', gap: 8 }}>
            <textarea
              ref={noteInputRef}
              value={newNote}
              onChange={e => setNewNote(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) submitNote(); }}
              placeholder="Type a call note…"
              rows={2}
              style={{ flex: 1, padding: '9px 12px', border: '1px solid #E2E8F0', borderRadius: 8, fontSize: 13, color: '#0e1b2c', resize: 'none', outline: 'none', fontFamily: 'inherit' }}
            />
            <button onClick={submitNote} disabled={noteLoading || !newNote.trim()}
              style={{ padding: '0 14px', background: noteLoading || !newNote.trim() ? '#a0aec0' : '#10b981', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: noteLoading || !newNote.trim() ? 'not-allowed' : 'pointer', alignSelf: 'flex-start', height: 38, whiteSpace: 'nowrap' }}>
              {noteLoading ? '…' : 'Add'}
            </button>
          </div>
          <div style={{ ...sectionBody, paddingTop: 8 }}>
            {notes.length === 0 ? (
              <div style={{ textAlign: 'center', color: '#a0aec0', fontSize: 13, padding: '16px 0' }}>No notes yet — add one above.</div>
            ) : notes.map(n => (
              <div key={n.id} style={{ padding: '12px 0', borderTop: '1px solid #EDF2F7', display: 'flex', gap: 12 }}>
                <div style={{ width: 30, height: 30, borderRadius: '50%', background: '#10b981', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 12, flexShrink: 0 }}>
                  {(n.author_name || '?').charAt(0).toUpperCase()}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3 }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#0e1b2c' }}>{n.author_name || 'Unknown'}</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={{ fontSize: 11, color: '#a0aec0' }}>{new Date(n.created_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                      <button onClick={() => deleteNote(n.id)} style={{ background: 'none', border: 'none', color: '#e74c3c', cursor: 'pointer', fontSize: 11, padding: 0 }}>Delete</button>
                    </div>
                  </div>
                  <div style={{ fontSize: 14, color: '#4b5a6e', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{n.note}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
        {/* ── Introducer Conversation — 6 Aug 2026 ──
            Was previously only reachable via the originating Lead row on
            /leads (and only until it got buried once "converted"). Same
            thread (lead_messages, keyed by deal.lead_id = the same row as
            introducer_leads.id), same component as the Leads page, just
            finally reachable from where a live deal is actually managed.
            Only introducer-sourced deals have a lead_id, so this is hidden
            entirely for direct/website-widget deals — nothing to message. */}
        {deal.lead_id && (
          <div style={sectionStyle}>
            <div style={sectionHead}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 15, color: '#0e1b2c' }}>Introducer Conversation</div>
                <div style={{ fontSize: 12, color: '#718096' }}>
                  {deal.introducer_name ? `Message ${deal.introducer_name} about this deal` : 'Message the introducer who referred this deal'}
                </div>
              </div>
              {deal.unread_introducer_messages > 0 && (
                <span style={{ background: '#ef4444', color: '#fff', fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20 }}>
                  {deal.unread_introducer_messages} new
                </span>
              )}
            </div>
            <div style={sectionBody}>
              <BrokerMessageThread
                leadId={deal.lead_id}
                onUnreadCleared={clearIntroducerUnread}
              />
            </div>
          </div>
        )}

        </div>

        <div style={{ display: 'grid', gridTemplateColumns: mobile ? '1fr' : '1fr 1fr', gap: 16, marginBottom: 16 }}>
        {/* ── Deal Value & Commissions ── */}
        <div style={sectionStyle}>
          <div style={sectionHead}>
            <span style={{ fontWeight: 700, fontSize: 15, color: '#0e1b2c' }}>Deal Value & Commissions</span>
            <span style={{ fontSize: 14, fontWeight: 700, color: '#10b981' }}>Total: £{allocTotal.toFixed(2)}</span>
          </div>
          <div style={sectionBody}>
            {/* Fee summary — lender_proc_fee and client_fee are inline-editable */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 20, padding: '12px 16px', background: '#f8fafc', borderRadius: 8, border: '1px solid #E2E8F0' }}>
              {/* Lender Proc Fee — editable */}
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', color: '#a0aec0', marginBottom: 3 }}>Lender Proc Fee</div>
                {editField === 'lender_proc_fee' ? (
                  <input
                    autoFocus
                    type="number"
                    step="0.01"
                    min="0"
                    value={editVal}
                    onChange={e => setEditVal(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') saveInlineField('lender_proc_fee', editVal); if (e.key === 'Escape') startEdit(null, ''); }}
                    onBlur={() => saveInlineField('lender_proc_fee', editVal)}
                    style={{ width: '100%', padding: '4px 6px', border: '1px solid #10b981', borderRadius: 6, fontSize: 15, fontWeight: 700, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' }}
                  />
                ) : (
                  <div
                    onClick={() => startEdit('lender_proc_fee', safeNum(deal.lender_proc_fee) || '')}
                    title="Click to edit"
                    style={{ fontSize: 16, fontWeight: 700, color: '#0e1b2c', cursor: 'text', display: 'flex', alignItems: 'center', gap: 4, padding: '2px 4px', borderRadius: 4, border: '1px solid transparent', transition: 'border-color 0.15s' }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = '#CBD5E0'}
                    onMouseLeave={e => e.currentTarget.style.borderColor = 'transparent'}
                  >
                    <span>£{safeNum(deal.lender_proc_fee).toFixed(2)}</span>
                    {savedField === 'lender_proc_fee'
                      ? <span style={{ fontSize: 11, color: '#10b981', fontWeight: 700 }}>✓</span>
                      : <span style={{ fontSize: 10, color: '#cbd5e0' }}>✏</span>}
                  </div>
                )}
              </div>
              {/* Client Fee — editable */}
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', color: '#a0aec0', marginBottom: 3 }}>Client Fee</div>
                {editField === 'client_fee' ? (
                  <input
                    autoFocus
                    type="number"
                    step="0.01"
                    min="0"
                    value={editVal}
                    onChange={e => setEditVal(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') saveInlineField('client_fee', editVal); if (e.key === 'Escape') startEdit(null, ''); }}
                    onBlur={() => saveInlineField('client_fee', editVal)}
                    style={{ width: '100%', padding: '4px 6px', border: '1px solid #10b981', borderRadius: 6, fontSize: 15, fontWeight: 700, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' }}
                  />
                ) : (
                  <div
                    onClick={() => startEdit('client_fee', safeNum(deal.client_fee) || '')}
                    title="Click to edit"
                    style={{ fontSize: 16, fontWeight: 700, color: '#0e1b2c', cursor: 'text', display: 'flex', alignItems: 'center', gap: 4, padding: '2px 4px', borderRadius: 4, border: '1px solid transparent', transition: 'border-color 0.15s' }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = '#CBD5E0'}
                    onMouseLeave={e => e.currentTarget.style.borderColor = 'transparent'}
                  >
                    <span>£{safeNum(deal.client_fee).toFixed(2)}</span>
                    {savedField === 'client_fee'
                      ? <span style={{ fontSize: 11, color: '#10b981', fontWeight: 700 }}>✓</span>
                      : <span style={{ fontSize: 10, color: '#cbd5e0' }}>✏</span>}
                  </div>
                )}
              </div>
              {/* Total — calculated, read-only */}
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', color: '#a0aec0', marginBottom: 3 }}>Total</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#10b981' }}>£{(safeNum(deal.lender_proc_fee) + safeNum(deal.client_fee)).toFixed(2)}</div>
              </div>
              {deal.fee_override && (
                <div>
                  <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', color: '#f59e0b', marginBottom: 3 }}>Override</div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: '#d97706' }}>£{safeNum(deal.fee_override).toFixed(2)}</div>
                </div>
              )}
            </div>

            {/* Allocations */}
            {allocations.length > 0 ? (
              <table className="alloc-table" style={{ marginBottom: 16 }}>
                <thead><tr><th>Tier</th><th>Recipient</th><th className="num">Amount</th><th>Status</th><th>Paid</th><th>Actions</th></tr></thead>
                <tbody>{allocations.map(a => <AllocationRow key={a.id} alloc={a} busy={busyAllocId === a.id} onUpdate={updateAllocationStatus} />)}</tbody>
              </table>
            ) : (
              <p style={{ color: '#a0aec0', fontSize: 13, marginBottom: 16 }}>No allocations yet — created automatically when fees are confirmed.</p>
            )}

            {/* Fee override */}
            {deal.fee_override && (
              <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: 13 }}>
                <strong>Override applied:</strong> £{safeNum(deal.fee_override).toFixed(2)} — {deal.override_reason}
                {deal.override_at && <span style={{ color: '#a0aec0', marginLeft: 8 }}>{new Date(deal.override_at).toLocaleDateString()}</span>}
              </div>
            )}
            {!showOverride ? (
              <button onClick={() => setShowOverride(true)} className="override-btn">📝 Override Fee</button>
            ) : (
              <div style={{ background: '#f8fafc', borderRadius: 8, padding: 16, border: '1px solid #E2E8F0' }}>
                <h3 style={{ margin: '0 0 14px', fontSize: 15, color: '#0e1b2c' }}>Override Commission</h3>
                <form onSubmit={handleSubmitOverride}>
                  <div className="form-group" style={{ marginBottom: 12 }}>
                    <label>New Amount (£) *</label>
                    <input type="number" name="fee_override" value={overrideForm.fee_override}
                      onChange={e => setOverrideForm({ ...overrideForm, fee_override: e.target.value })}
                      placeholder="Enter new amount" step="0.01" required />
                    <small>Current: £{safeNum(deal.actual_fee).toFixed(2)}</small>
                  </div>
                  <div className="form-group" style={{ marginBottom: 14 }}>
                    <label>Reason *</label>
                    <textarea name="override_reason" value={overrideForm.override_reason}
                      onChange={e => setOverrideForm({ ...overrideForm, override_reason: e.target.value })}
                      placeholder="e.g. Client dispute, correction, special arrangement" rows={3} required />
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button type="submit" disabled={submitting} className="submit-btn">{submitting ? 'Submitting…' : 'Confirm Override'}</button>
                    <button type="button" onClick={() => setShowOverride(false)} className="cancel-btn">Cancel</button>
                  </div>
                </form>
              </div>
            )}
          </div>
        </div>

        {/* ── DIP Certificate ── */}
        {(() => {
          const dipStage = allStages.find(s => s.name.toLowerCase().includes('dip'));
          if (!dipStage) return null;
          return (
            <div style={sectionStyle}>
              <div style={sectionHead}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontWeight: 700, fontSize: 15, color: '#0e1b2c' }}>DIP Certificate</span>
                  {dipDoc && <span style={{ background: '#d1fae5', color: '#065f46', fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 9999 }}>Uploaded</span>}
                  {dipDoc?.visible_to_introducer && <span style={{ background: '#dbeafe', color: '#1e40af', fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 9999 }}>Visible to Introducer</span>}
                </div>
                <button onClick={() => setShowDipPanel(!showDipPanel)} style={{ background: '#f3f4f6', border: 'none', borderRadius: 8, padding: '7px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer', color: '#374151' }}>
                  {dipDoc ? 'Replace DIP' : 'Upload DIP'}
                </button>
              </div>
              <div style={sectionBody}>
                {dipDoc && !showDipPanel && dipDoc.expires_at && (() => {
                  const daysLeft = Math.ceil((new Date(dipDoc.expires_at) - new Date()) / 86400000);
                  if (daysLeft <= 0) return <div style={{ background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: 13 }}><strong style={{ color: '#b91c1c' }}>⚠️ DIP certificate has expired.</strong></div>;
                  if (daysLeft <= 7) return <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: 13 }}><strong style={{ color: '#92400e' }}>⏳ DIP expires in {daysLeft} day{daysLeft === 1 ? '' : 's'}.</strong></div>;
                  return null;
                })()}
                {dipDoc && !showDipPanel && (
                  <div style={{ background: '#f8fafc', borderRadius: 8, padding: '10px 14px', display: 'flex', gap: 20, flexWrap: 'wrap', fontSize: 13 }}>
                    <div><span style={{ color: '#718096' }}>File: </span><strong>{dipDoc.original_filename}</strong></div>
                    {dipDoc.reference && <div><span style={{ color: '#718096' }}>Ref: </span><strong>{dipDoc.reference}</strong></div>}
                    {dipDoc.expires_at && <div><span style={{ color: '#718096' }}>Expires: </span><strong>{new Date(dipDoc.expires_at).toLocaleDateString('en-GB')}</strong></div>}
                  </div>
                )}
                {showDipPanel && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <input type="file" ref={dipFileRef} accept=".pdf,.jpg,.jpeg,.png" style={{ display: 'none' }} onChange={e => setDipFile(e.target.files[0] || null)} />
                    <div onClick={() => dipFileRef.current.click()} style={{ border: '2px dashed #CBD5E0', borderRadius: 8, padding: 20, textAlign: 'center', cursor: 'pointer', color: '#718096', fontSize: 13 }}>
                      {dipFile ? <strong style={{ color: '#10b981' }}>✓ {dipFile.name}</strong> : 'Click to select DIP (PDF/image)'}
                    </div>
                    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                      <div style={{ flex: 1, minWidth: 140 }}>
                        <label style={{ fontSize: 12, color: '#718096', display: 'block', marginBottom: 4 }}>DIP Reference</label>
                        <input value={dipForm.reference} onChange={e => setDipForm({ ...dipForm, reference: e.target.value })} placeholder="e.g. DIP-123456" style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', border: '1px solid #E2E8F0', borderRadius: 6, fontSize: 13 }} />
                      </div>
                      <div style={{ flex: 1, minWidth: 140 }}>
                        <label style={{ fontSize: 12, color: '#718096', display: 'block', marginBottom: 4 }}>Expiry Date</label>
                        <input type="date" value={dipForm.expiry} onChange={e => setDipForm({ ...dipForm, expiry: e.target.value })} style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', border: '1px solid #E2E8F0', borderRadius: 6, fontSize: 13 }} />
                      </div>
                    </div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                      <input type="checkbox" checked={dipForm.visible} onChange={e => setDipForm({ ...dipForm, visible: e.target.checked })} />
                      Make visible to introducer
                    </label>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button onClick={uploadDip} disabled={!dipFile || dipUploading} style={{ background: !dipFile || dipUploading ? '#a0aec0' : '#10b981', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 20px', fontSize: 13, fontWeight: 600, cursor: !dipFile || dipUploading ? 'not-allowed' : 'pointer' }}>{dipUploading ? 'Uploading…' : 'Save DIP'}</button>
                      <button onClick={() => { setShowDipPanel(false); setDipFile(null); }} style={{ background: '#f3f4f6', border: 'none', borderRadius: 8, padding: '9px 16px', fontSize: 13, cursor: 'pointer', color: '#374151', fontWeight: 600 }}>Cancel</button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        </div>

        {/* ── Client Document Portal ── */}
        <div className="dd-section" style={{ ...sectionStyle, overflow: 'visible' }}>
          <div style={sectionHead}>
            <span style={{ fontWeight: 700, fontSize: 15, color: '#0e1b2c' }}>Client Document Portal</span>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {clientDocs?.invite?.accepted_at && (
                <button onClick={sendReminder} style={{ background: reminderSent ? '#d1fae5' : '#f3f4f6', border: 'none', borderRadius: 8, padding: '7px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer', color: reminderSent ? '#065f46' : '#374151' }}>{reminderSent ? 'Reminder Sent!' : 'Send Reminder'}</button>
              )}
              <button onClick={() => setShowBrokerUpload(true)} style={{ background: '#f3f4f6', border: 'none', borderRadius: 8, padding: '7px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer', color: '#374151' }}>Send to Client</button>
              {docInviteStatusLoaded && !docInviteEnabled ? (
                <span title="Ask your platform administrator to enable this feature for your company" style={{ fontSize: 12, color: '#9ca3af', alignSelf: 'center', fontStyle: 'italic' }}>
                  Client invites not enabled for your company
                </span>
              ) : (
                <button onClick={() => openInviteModal()} disabled={!docInviteStatusLoaded} style={{ background: '#1a56db', color: 'white', border: 'none', borderRadius: 8, padding: '7px 18px', fontSize: 13, fontWeight: 600, cursor: docInviteStatusLoaded ? 'pointer' : 'default', opacity: docInviteStatusLoaded ? 1 : 0.6 }}>{clientDocs?.invite ? 'Re-send Invite' : 'Invite Client'}</button>
              )}
            </div>
          </div>
          <div style={sectionBody}>
            {clientDocs?.invite && (
              <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '10px 16px', marginBottom: 14, fontSize: 13, color: '#065f46' }}>
                Invite sent to <strong>{clientDocs.invite.client_email}</strong> on {new Date(clientDocs.invite.created_at).toLocaleDateString('en-GB')}
                {clientDocs.invite.accepted_at ? <span style={{ marginLeft: 8, fontWeight: 600 }}> — Account created</span> : <span style={{ marginLeft: 8, color: '#92400e' }}> — Awaiting account setup</span>}
              </div>
            )}
            {clientDocs?.documents?.filter(d => d.direction !== 'broker_upload').length > 0 && (() => {
              const docs = clientDocs.documents.filter(d => d.direction !== 'broker_upload');
              const approved = docs.filter(d => d.broker_status === 'approved').length;
              const rejected = docs.filter(d => d.broker_status === 'rejected').length;
              const pending  = docs.length - approved - rejected;
              return (
                <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
                  {[['Approved', approved, '#d1fae5', '#065f46'], ['Awaiting Review', pending, '#dbeafe', '#1e40af'], ['Rejected', rejected, '#fee2e2', '#991b1b']].map(([label, count, bg, color]) => (
                    <div key={label} style={{ background: bg, borderRadius: 8, padding: '7px 14px', fontSize: 13, fontWeight: 600, color }}>{count} {label}</div>
                  ))}
                </div>
              );
            })()}
            {clientDocs?.documents?.filter(d => d.direction !== 'broker_upload').length > 0 ? (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginBottom: 14 }}>
                <thead><tr style={{ borderBottom: '2px solid #e5e7eb', textAlign: 'left' }}><th style={{ padding: '8px 12px', color: '#374151' }}>Document</th><th style={{ padding: '8px 12px', color: '#374151' }}>Status</th><th style={{ padding: '8px 12px', color: '#374151' }}>Uploaded</th><th style={{ padding: '8px 12px', color: '#374151' }}>Actions</th></tr></thead>
                <tbody>
                  {clientDocs.documents.filter(d => d.direction !== 'broker_upload').map(doc => {
                    const bStatus = doc.broker_status || 'pending';
                    const busy = reviewBusy[doc.id];
                    return (
                      <tr key={doc.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                        <td style={{ padding: '10px 12px', fontWeight: 500, color: '#0e1b2c' }}>
                          <div>{doc.document_type_name || doc.original_filename}</div>
                          {bStatus === 'rejected' && doc.broker_note && <div style={{ fontSize: 11, color: '#dc2626', marginTop: 3 }}>Reason: {doc.broker_note}</div>}
                        </td>
                        <td style={{ padding: '10px 12px' }}>
                          <span style={{ background: bStatus === 'approved' ? '#d1fae5' : bStatus === 'rejected' ? '#fee2e2' : '#dbeafe', color: bStatus === 'approved' ? '#065f46' : bStatus === 'rejected' ? '#991b1b' : '#1e40af', padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600 }}>
                            {bStatus === 'approved' ? 'Approved' : bStatus === 'rejected' ? 'Rejected' : 'Awaiting Review'}
                          </span>
                        </td>
                        <td style={{ padding: '10px 12px', color: '#718096' }}>{doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString('en-GB') : '—'}</td>
                        <td style={{ padding: '10px 12px' }}>
                          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            <button onClick={() => dlDoc(doc.id, doc.original_filename)} style={{ background: 'none', border: 'none', color: '#1a56db', fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: 0 }}>Download</button>
                            {bStatus !== 'approved' && <button onClick={() => reviewDoc(doc.id, 'approved', null)} disabled={busy} style={{ background: '#d1fae5', border: 'none', borderRadius: 6, padding: '3px 10px', fontSize: 12, fontWeight: 600, color: '#065f46', cursor: 'pointer' }}>{busy ? '…' : 'Approve'}</button>}
                            {bStatus !== 'rejected' && <button onClick={() => setShowRejectModal(doc.id)} disabled={busy} style={{ background: '#fee2e2', border: 'none', borderRadius: 6, padding: '3px 10px', fontSize: 12, fontWeight: 600, color: '#991b1b', cursor: 'pointer' }}>Reject</button>}
                            {bStatus !== 'pending' && <button onClick={() => reviewDoc(doc.id, 'pending', null)} disabled={busy} style={{ background: 'none', border: '1px solid #d1d5db', borderRadius: 6, padding: '3px 8px', fontSize: 11, color: '#718096', cursor: 'pointer' }}>Reset</button>}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <p style={{ color: '#9ca3af', fontSize: 13, marginBottom: 14 }}>{clientDocs?.invite ? 'No documents uploaded yet.' : 'Invite the client to upload their documents.'}</p>
            )}
            {clientDocs?.documents?.filter(d => d.direction === 'broker_upload').length > 0 && (
              <div style={{ marginBottom: 14 }}>
                <p style={{ fontWeight: 600, fontSize: 13, color: '#374151', margin: '0 0 8px' }}>Documents sent to client:</p>
                {clientDocs.documents.filter(d => d.direction === 'broker_upload').map(doc => (
                  <div key={doc.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', background: '#f8fafc', borderRadius: 8, marginBottom: 6 }}>
                    <span style={{ fontSize: 13, color: '#0e1b2c' }}>{doc.original_filename}</span>
                    <button onClick={() => dlDoc(doc.id, doc.original_filename)} style={{ background: 'none', border: 'none', color: '#1a56db', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Download</button>
                  </div>
                ))}
              </div>
            )}
            <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: 14 }}>
              <p style={{ fontWeight: 600, fontSize: 13, color: '#374151', margin: '0 0 10px' }}>Messages with client</p>
              {portalMsgs.length > 0 ? (
                <div style={{ maxHeight: 200, overflowY: 'auto', marginBottom: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {portalMsgs.map(m => (
                    <div key={m.id} style={{ display: 'flex', justifyContent: m.sender_role === 'broker' ? 'flex-end' : 'flex-start' }}>
                      <div style={{ maxWidth: '70%', background: m.sender_role === 'broker' ? '#1a56db' : '#f3f4f6', color: m.sender_role === 'broker' ? 'white' : '#0e1b2c', borderRadius: 12, padding: '8px 14px', fontSize: 13 }}>
                        <div>{m.message}</div>
                        <div style={{ fontSize: 11, opacity: 0.7, marginTop: 4 }}>{new Date(m.created_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : <p style={{ fontSize: 13, color: '#9ca3af', marginBottom: 10 }}>No messages yet.</p>}
              {clientDocs?.invite?.accepted_at && (
                <div style={{ display: 'flex', gap: 8 }}>
                  <input value={newMsg} onChange={e => setNewMsg(e.target.value)} onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendPortalMsg()}
                    placeholder="Type a message to the client…" style={{ flex: 1, border: '1px solid #d1d5db', borderRadius: 8, padding: '9px 14px', fontSize: 13, outline: 'none' }} />
                  <button onClick={sendPortalMsg} disabled={sendingMsg || !newMsg.trim()} style={{ background: '#1a56db', color: 'white', border: 'none', borderRadius: 8, padding: '9px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: !newMsg.trim() ? 0.5 : 1 }}>Send</button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Stage History ── */}
        <div style={sectionStyle}>
          <div style={sectionHead}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontWeight: 700, fontSize: 15, color: '#0e1b2c' }}>Stage History</span>
              <span style={{ background: '#EDF2F7', color: '#718096', fontSize: 12, fontWeight: 600, padding: '2px 8px', borderRadius: 9999 }}>{stageHistory.length}</span>
            </div>
          </div>
          <div style={sectionBody}>
            {stageHistory.length === 0 ? (
              <div style={{ textAlign: 'center', color: '#a0aec0', fontSize: 13, padding: '12px 0' }}>No stage changes recorded yet.</div>
            ) : (
              <div style={{ position: 'relative' }}>
                <div style={{ position: 'absolute', left: 15, top: 8, bottom: 8, width: 2, background: '#E2E8F0', zIndex: 0 }} />
                {stageHistory.map((h, idx) => (
                  <div key={h.id} style={{ display: 'flex', gap: 14, marginBottom: idx < stageHistory.length - 1 ? 14 : 0, position: 'relative', zIndex: 1 }}>
                    <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#0e1b2c', border: '3px solid #10b981', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="3"><polyline points="9 18 15 12 9 6" /></svg>
                    </div>
                    <div style={{ flex: 1, paddingTop: 6 }}>
                      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6, marginBottom: 2 }}>
                        {h.from_stage_name && <><span style={{ fontSize: 12, background: '#F7FAFC', border: '1px solid #E2E8F0', borderRadius: 4, padding: '2px 8px', color: '#718096' }}>{h.from_stage_name}</span><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#a0aec0" strokeWidth="2.5"><polyline points="9 18 15 12 9 6" /></svg></>}
                        <span style={{ fontSize: 12, background: '#d1fae5', border: '1px solid #6ee7b7', borderRadius: 4, padding: '2px 8px', color: '#065f46', fontWeight: 600 }}>{h.to_stage_name || 'Unknown'}</span>
                      </div>
                      <div style={{ fontSize: 11, color: '#a0aec0' }}>
                        {h.changed_by_name && <span>{h.changed_by_name} · </span>}
                        {new Date(h.changed_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ── Partner Referrals ── */}
        <div style={sectionStyle}>
          <div style={sectionHead}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 15, color: '#0e1b2c' }}>Partner Referrals</div>
              <div style={{ fontSize: 12, color: '#718096' }}>Log referrals to third-party services for this deal</div>
            </div>
            {availablePartners.length > 0 && <button onClick={() => { setShowRefForm(true); setRefError(''); }} style={{ background: '#10b981', color: '#fff', border: 'none', borderRadius: 8, padding: '7px 16px', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>+ Add Referral</button>}
          </div>
          <div style={sectionBody}>
            {availablePartners.length === 0 ? (
              <div style={{ fontSize: 13, color: '#9ca3af' }}>No partner services enabled. Contact your platform administrator.</div>
            ) : referrals.length === 0 && !showRefForm ? (
              <div style={{ fontSize: 13, color: '#9ca3af' }}>No referrals logged for this deal.</div>
            ) : null}
            {referrals.length > 0 && (
              <div style={{ display: 'grid', gap: 10, marginBottom: showRefForm ? 16 : 0 }}>
                {referrals.map(ref => {
                  const statusCols = { pending: ['#fef3c7', '#92400e'], accepted: ['#dbeafe', '#1e40af'], completed: ['#d1fae5', '#065f46'], paid: ['#dcfce7', '#166534'] };
                  const sEntry = statusCols[ref.status] || ['#f3f4f6', '#6b7280'];
                  const nextStatuses = { pending: ['accepted'], accepted: ['completed'], completed: ['paid'] };
                  const nexts = nextStatuses[ref.status] || [];
                  return (
                    <div key={ref.id} style={{ background: '#f9fafb', borderRadius: 8, padding: '12px 14px', display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                      <span style={{ fontSize: 20, flexShrink: 0 }}>{ref.category_icon}</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <span style={{ fontWeight: 600, fontSize: 13 }}>{ref.partner_name}</span>
                          <span style={{ fontSize: 11, color: '#6b7280' }}>{ref.category_name}</span>
                          <span style={{ background: sEntry[0], color: sEntry[1], fontSize: 11, fontWeight: 700, padding: '1px 8px', borderRadius: 999, textTransform: 'capitalize' }}>{ref.status}</span>
                          {ref.commission_amount && <span style={{ fontSize: 11, color: '#065f46', fontWeight: 600 }}>+£{parseFloat(ref.commission_amount).toFixed(2)}</span>}
                        </div>
                        <div style={{ fontSize: 12, color: '#374151', marginTop: 4 }}>Client: {ref.client_name}</div>
                        {ref.notes && <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{ref.notes}</div>}
                        <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>{new Date(ref.created_at).toLocaleDateString('en-GB')}{ref.referred_by_name ? ' · ' + ref.referred_by_name : ''}</div>
                        {nexts.length > 0 && (
                          <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                            {nexts.map(ns => <button key={ns} disabled={!!refUpdating[ref.id]} onClick={() => updateReferralStatus(ref.id, ns)} style={{ background: '#0e1b2c', color: '#fff', border: 'none', borderRadius: 6, padding: '4px 12px', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>Mark {ns}</button>)}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {showRefForm && (
              <div style={{ paddingTop: referrals.length ? 16 : 0, borderTop: referrals.length ? '1px solid #e5e7eb' : 'none' }}>
                <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 12 }}>New Referral</div>
                {refError && <div style={{ background: '#fee2e2', color: '#991b1b', padding: '8px 12px', borderRadius: 6, fontSize: 12, marginBottom: 10 }}>{refError}</div>}
                <form onSubmit={submitReferral}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 14px' }}>
                    <div style={{ marginBottom: 12 }}>
                      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Partner *</label>
                      <select required value={refForm.partner_id} onChange={e => setRefForm(f => ({ ...f, partner_id: e.target.value }))} style={{ width: '100%', padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, fontFamily: 'inherit' }}>
                        <option value="">Select partner...</option>
                        {availablePartners.map(p => <option key={p.id} value={p.id}>{p.category_icon} {p.name} ({p.category_name})</option>)}
                      </select>
                    </div>
                    <div style={{ marginBottom: 12 }}>
                      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Client name *</label>
                      <input required value={refForm.client_name} onChange={e => setRefForm(f => ({ ...f, client_name: e.target.value }))} style={{ width: '100%', padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, fontFamily: 'inherit' }} />
                    </div>
                    <div style={{ marginBottom: 12 }}>
                      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Commission (£)</label>
                      <input type="number" step="0.01" min="0" value={refForm.commission_amount} onChange={e => setRefForm(f => ({ ...f, commission_amount: e.target.value }))} style={{ width: '100%', padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, fontFamily: 'inherit' }} />
                    </div>
                    <div style={{ marginBottom: 12 }}>
                      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Notes</label>
                      <input value={refForm.notes} onChange={e => setRefForm(f => ({ ...f, notes: e.target.value }))} style={{ width: '100%', padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, fontFamily: 'inherit' }} />
                    </div>
                  </div>
                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 12, cursor: 'pointer', fontSize: 13 }}>
                    <input type="checkbox" checked={refForm.client_disclosure} onChange={e => setRefForm(f => ({ ...f, client_disclosure: e.target.checked }))} style={{ marginTop: 2, flexShrink: 0 }} />
                    <span>I confirm the client has been informed of this referral and any associated fee arrangement (FCA requirement)</span>
                  </label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button type="submit" disabled={refSaving} style={{ background: '#10b981', color: '#fff', border: 'none', borderRadius: 7, padding: '8px 18px', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>{refSaving ? 'Logging…' : 'Log Referral'}</button>
                    <button type="button" onClick={() => setShowRefForm(false)} style={{ background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: 7, padding: '8px 18px', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>Cancel</button>
                  </div>
                </form>
              </div>
            )}
          </div>
        </div>

        {/* ── Modals ── */}
        {showStatusModal && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ background: '#fff', borderRadius: 12, padding: 28, maxWidth: 440, width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
              <h3 style={{ margin: '0 0 8px', fontSize: 17, color: '#0e1b2c' }}>{showStatusModal === 'not_proceeding' ? 'Mark as Not Proceeding' : 'Archive Deal'}</h3>
              <p style={{ margin: '0 0 16px', fontSize: 13, color: '#6b7280' }}>{showStatusModal === 'not_proceeding' ? 'This will move the commission stage to Cancelled. You can reopen the deal later.' : 'Archived deals are hidden from the main list. You can reopen to restore.'}</p>
              <textarea value={statusNote} onChange={e => setStatusNote(e.target.value)} placeholder="Reason (optional)…" rows={3} style={{ width: '100%', border: '1px solid #CBD5E0', borderRadius: 8, padding: '10px 12px', fontSize: 13, resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' }} />
              {statusError && <p style={{ color: '#dc2626', fontSize: 13, margin: '8px 0 0' }}>{statusError}</p>}
              <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
                <button onClick={() => updateStatus(showStatusModal === 'archive' ? 'archived' : 'not_proceeding')} disabled={statusBusy} style={{ background: showStatusModal === 'archive' ? '#dc2626' : '#f59e0b', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', fontWeight: 600, fontSize: 14, cursor: statusBusy ? 'not-allowed' : 'pointer', opacity: statusBusy ? 0.7 : 1 }}>{statusBusy ? 'Saving…' : showStatusModal === 'archive' ? 'Archive Deal' : 'Confirm'}</button>
                <button onClick={() => setShowStatusModal(false)} style={{ background: '#f3f4f6', border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: 14, cursor: 'pointer', color: '#374151' }}>Cancel</button>
              </div>
            </div>
          </div>
        )}

        {showReassign && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={e => { if (e.target === e.currentTarget) setShowReassign(false); }}>
            <div style={{ background: '#fff', borderRadius: 12, padding: 28, maxWidth: 420, width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
              <h3 style={{ margin: '0 0 8px', fontSize: 17, color: '#0e1b2c' }}>Reassign Deal</h3>
              <p style={{ margin: '0 0 16px', fontSize: 13, color: '#6b7280' }}>Move this deal to a different adviser or manager. They'll see it in their own deal list and pipeline immediately.</p>
              {advisersLoading ? (
                <p style={{ fontSize: 13, color: '#a0aec0' }}>Loading team…</p>
              ) : advisers.length === 0 ? (
                <p style={{ fontSize: 13, color: '#a0aec0' }}>No other active team members found.</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 260, overflowY: 'auto' }}>
                  {advisers.map(a => (
                    <button key={a.id} onClick={() => reassignDeal(a.id)} disabled={reassignBusy}
                      style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        textAlign: 'left', padding: '9px 12px', borderRadius: 8,
                        border: a.id === deal.created_by_user_id ? '1px solid #10b981' : '1px solid #E2E8F0',
                        background: a.id === deal.created_by_user_id ? '#ecfdf5' : '#fff',
                        cursor: reassignBusy ? 'not-allowed' : 'pointer', fontSize: 13, fontFamily: 'inherit',
                      }}>
                      <span>
                        <strong style={{ color: '#0e1b2c' }}>{a.name}</strong>
                        <span style={{ color: '#a0aec0', marginLeft: 6, fontSize: 12 }}>({a.broker_level || a.role})</span>
                      </span>
                      {a.id === deal.created_by_user_id && <span style={{ fontSize: 11, color: '#10b981', fontWeight: 700 }}>Current</span>}
                    </button>
                  ))}
                </div>
              )}
              {reassignError && <p style={{ color: '#dc2626', fontSize: 13, margin: '12px 0 0' }}>{reassignError}</p>}
              <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
                <button onClick={() => setShowReassign(false)} style={{ background: '#f3f4f6', border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: 14, cursor: 'pointer', color: '#374151' }}>Cancel</button>
              </div>
            </div>
          </div>
        )}

        {showRejectModal && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={e => { if (e.target === e.currentTarget) setShowRejectModal(null); }}>
            <div style={{ background: 'white', borderRadius: 12, padding: '28px 32px', width: '100%', maxWidth: 420, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
              <h3 style={{ margin: '0 0 8px', fontSize: 18, color: '#0e1b2c' }}>Reject Document</h3>
              <p style={{ margin: '0 0 16px', fontSize: 13, color: '#718096' }}>Provide a reason so the client knows what to fix. They'll receive an email notification.</p>
              <textarea value={rejectNote} onChange={e => setRejectNote(e.target.value)} placeholder="e.g. This statement is over 3 months old…" style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: 8, padding: '10px 14px', fontSize: 13, minHeight: 80, boxSizing: 'border-box', resize: 'vertical' }} />
              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 16 }}>
                <button onClick={() => { setShowRejectModal(null); setRejectNote(''); }} style={{ background: '#f3f4f6', border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: 14, cursor: 'pointer' }}>Cancel</button>
                <button onClick={() => reviewDoc(showRejectModal, 'rejected', rejectNote)} style={{ background: '#dc2626', color: 'white', border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>Reject & Notify</button>
              </div>
            </div>
          </div>
        )}

        {showBrokerUpload && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={e => { if (e.target === e.currentTarget) setShowBrokerUpload(false); }}>
            <div style={{ background: 'white', borderRadius: 12, padding: '28px 32px', width: '100%', maxWidth: 420, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
              <h3 style={{ margin: '0 0 8px', fontSize: 18, color: '#0e1b2c' }}>Send Document to Client</h3>
              <p style={{ margin: '0 0 16px', fontSize: 13, color: '#718096' }}>The document will appear in the client's portal and they'll receive an email notification.</p>
              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Label (optional)</label>
                <input value={brokerUploadLabel} onChange={e => setBrokerUploadLabel(e.target.value)} placeholder="e.g. Mortgage offer letter" style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: 8, padding: '9px 14px', fontSize: 13, boxSizing: 'border-box' }} />
              </div>
              <input type="file" ref={brokerFileRef} style={{ display: 'none' }} onChange={e => brokerUpload(e.target.files[0])} />
              <button onClick={() => brokerFileRef.current?.click()} disabled={brokerUploading} style={{ width: '100%', background: '#1a56db', color: 'white', border: 'none', borderRadius: 8, padding: 11, fontSize: 14, fontWeight: 600, cursor: 'pointer', opacity: brokerUploading ? 0.6 : 1 }}>{brokerUploading ? 'Uploading…' : 'Choose File & Upload'}</button>
              <button onClick={() => setShowBrokerUpload(false)} style={{ width: '100%', background: 'none', border: 'none', marginTop: 10, fontSize: 13, color: '#718096', cursor: 'pointer' }}>Cancel</button>
            </div>
          </div>
        )}

        {showInviteModal && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={e => { if (e.target === e.currentTarget) setShowInviteModal(false); }}>
            <div style={{ background: 'white', borderRadius: 12, padding: '28px 32px', width: '100%', maxWidth: 480, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
              <h3 style={{ margin: '0 0 8px', fontSize: 18, color: '#0e1b2c' }}>Invite {inviteTargetName || 'Client'}</h3>
              <p style={{ margin: '0 0 20px', fontSize: 13, color: '#718096' }}>{inviteTargetName || 'The client'} will receive a secure link to set up their own login and upload the selected documents.</p>
              <div style={{ marginBottom: 20 }}>
                <p style={{ fontWeight: 600, fontSize: 13, color: '#374151', marginBottom: 10 }}>Select documents to request:</p>
                {inviteDocTypes.map(dt => (
                  <label key={dt.id} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, cursor: 'pointer', fontSize: 14, color: '#0e1b2c' }}>
                    <input type="checkbox" checked={selectedDocs.includes(dt.id)} onChange={e => setSelectedDocs(e.target.checked ? [...selectedDocs, dt.id] : selectedDocs.filter(id => id !== dt.id))} />
                    {dt.name}
                    {dt.is_required && <span style={{ fontSize: 11, color: '#dc2626', fontWeight: 600 }}>Required</span>}
                  </label>
                ))}
              </div>
              {inviteError && <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 14px', color: '#dc2626', fontSize: 13, marginBottom: 14 }}>{inviteError}</div>}
              {inviteSuccess && <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '10px 14px', color: '#065f46', fontSize: 13, marginBottom: 14 }}>{inviteSuccess}</div>}
              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                <button onClick={() => setShowInviteModal(false)} style={{ background: '#f3f4f6', border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: 14, cursor: 'pointer' }}>Close</button>
                {!inviteSuccess && <button onClick={sendInvite} disabled={inviting} style={{ background: '#1a56db', color: 'white', border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: 14, fontWeight: 600, cursor: inviting ? 'not-allowed' : 'pointer', opacity: inviting ? 0.6 : 1 }}>{inviting ? 'Sending…' : 'Send Invite'}</button>}
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────

function StatusBadge({ status }) {
  return <span className={`status-badge status-${status}`}>{STATUS_LABELS[status] || status}</span>;
}

function AllocationActions({ alloc, busy, onUpdate }) {
  if (alloc.status === 'pipeline') {
    return (
      <div className="alloc-actions">
        <button className="btn-payable" disabled={busy} onClick={() => onUpdate(alloc.id, 'payable')}>{busy ? '…' : 'Mark Payable'}</button>
        <button className="btn-cancel" disabled={busy} onClick={() => onUpdate(alloc.id, 'cancelled', { confirmMessage: `Cancel the ${TIER_LABELS[alloc.tier]} allocation of £${safeNum(alloc.amount).toFixed(2)}? This cannot be undone.` })}>Cancel</button>
      </div>
    );
  }
  if (alloc.status === 'payable') {
    return (
      <div className="alloc-actions">
        <button className="btn-paid" disabled={busy} onClick={() => onUpdate(alloc.id, 'paid', { confirmMessage: `Confirm payment of £${safeNum(alloc.amount).toFixed(2)} to ${TIER_LABELS[alloc.tier]}? Paid status cannot be reversed.` })}>{busy ? '…' : 'Mark Paid'}</button>
      </div>
    );
  }
  return <span className="alloc-actions-empty">—</span>;
}

function AllocationRow({ alloc, busy, onUpdate }) {
  return (
    <tr>
      <td>{TIER_LABELS[alloc.tier] || alloc.tier}</td>
      <td>{alloc.user_name || <span className="muted">Unassigned</span>}</td>
      <td className="num">£{safeNum(alloc.amount).toFixed(2)}</td>
      <td><StatusBadge status={alloc.status} /></td>
      <td>{alloc.paid_at ? new Date(alloc.paid_at).toLocaleDateString() : <span className="muted">—</span>}</td>
      <td><AllocationActions alloc={alloc} busy={busy} onUpdate={onUpdate} /></td>
    </tr>
  );
}
